import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { ASC } from 'app/shared/util/pagination.constants';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IMailGroup, defaultValue } from 'app/shared/model/mail-group.model';
import { BaseResponseModel } from 'app/shared/model/base-response.model';

const initialState: EntityState<IMailGroup> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
};

const apiUrl = 'api/mail-groups';

type GetEntities = IQueryParams & {
  isFetchAllType?: boolean;
};
// Actions

export const getEntities = createAsyncThunk(
  'mailGroup/fetch_entity_list',
  async ({ sort, isFetchAllType }: GetEntities) => {
    const requestUrl = `${apiUrl}?${sort ? `sort=${sort}&` : ''}${isFetchAllType ? `isFetchAllType=${isFetchAllType}&` : ''}cacheBuster=${new Date().getTime()}`;
    return axios.get<BaseResponseModel<IMailGroup[]>>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'mailGroup/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<BaseResponseModel<IMailGroup>>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'mailGroup/create_entity',
  async (entity: IMailGroup, thunkAPI) => {
    const result = await axios.post<IMailGroup>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'mailGroup/update_entity',
  async (entity: IMailGroup, thunkAPI) => {
    const result = await axios.put<IMailGroup>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const partialUpdateEntity = createAsyncThunk(
  'mailGroup/partial_update_entity',
  async (entity: IMailGroup, thunkAPI) => {
    const result = await axios.patch<IMailGroup>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'mailGroup/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IMailGroup>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const MailGroupSlice = createEntitySlice({
  name: 'mailGroup',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntity), (state, action) => {
        let allEntities = [...state.entities];

        allEntities = allEntities.map(item => {
          if (item.id === action.payload.data.data.id) {
            return action.payload.data.data;
          } else {
            return item;
          }
        });

        return {
          ...state,
          loading: false,
          entity: action.payload.data.data,
          entities: allEntities,
        };
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data } = action.payload.data;
        return {
          ...state,
          loading: false,
          entities: data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = MailGroupSlice.actions;

// Reducer
export default MailGroupSlice.reducer;
